import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { policiesService } from "../../services/policies";

const initialState = {
  items: [],
  selectedPolicy: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    perPage: 10,
  },
};

export const fetchPolicies = createAsyncThunk(
  "policies/fetchPolicies",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await policiesService.fetchPolicies(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch policies");
    }
  }
);

export const fetchPolicyDetails = createAsyncThunk(
  "policies/fetchPolicyDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await policiesService.fetchPolicyDetails(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch policy details");
    }
  }
);

export const createPolicy = createAsyncThunk(
  "policies/createPolicy",
  async (data, { rejectWithValue }) => {
    try {
      const response = await policiesService.createPolicy(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create policy");
    }
  }
);

export const updatePolicy = createAsyncThunk(
  "policies/updatePolicy",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await policiesService.updatePolicy(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update policy");
    }
  }
);

export const deletePolicy = createAsyncThunk(
  "policies/deletePolicy",
  async (id, { rejectWithValue }) => {
    try {
      const response = await policiesService.deletePolicy(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete policy");
    }
  }
);

export const importPoliciesFromClaims = createAsyncThunk(
  "policies/importPoliciesFromClaims",
  async (_, { rejectWithValue }) => {
    try {
      const response = await policiesService.importPoliciesFromClaims();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to import policies");
    }
  }
);

export const syncClaimFormPolicies = createAsyncThunk(
  "policies/syncClaimFormPolicies",
  async (_, { rejectWithValue }) => {
    try {
      const response = await policiesService.syncClaimFormPolicies();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to sync claim form policies");
    }
  }
);

const policySlice = createSlice({
  name: "policies",
  initialState,
  reducers: {
    selectPolicy: (state, action) => {
      state.selectedPolicy = action.payload;
    },
    clearSelectedPolicy: (state) => {
      state.selectedPolicy = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both wrapped and unwrapped responses
        const payload = action.payload;
        if (payload.data && Array.isArray(payload.data)) {
          state.items = payload.data;
        } else if (Array.isArray(payload)) {
          state.items = payload;
        } else {
          state.items = [];
        }
        state.pagination = {
          currentPage: payload.current_page || 1,
          totalPages: payload.last_page || 1,
          perPage: payload.per_page || 10,
        };
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(fetchPolicyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPolicy = action.payload.data || action.payload;
      })
      .addCase(fetchPolicyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPolicy.fulfilled, (state, action) => {
        state.loading = false;
        const newPolicy = action.payload.data || action.payload;
        state.items.unshift(newPolicy);
      })
      .addCase(createPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updatePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPolicy = action.payload.data || action.payload;
        const index = state.items.findIndex((p) => p.id === updatedPolicy.id);
        if (index !== -1) {
          state.items[index] = updatedPolicy;
        }
        if (state.selectedPolicy?.id === updatedPolicy.id) {
          state.selectedPolicy = updatedPolicy;
        }
      })
      .addCase(updatePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deletePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((p) => p.id !== action.payload.id);
        if (state.selectedPolicy?.id === action.payload.id) {
          state.selectedPolicy = null;
        }
      })
      .addCase(deletePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(importPoliciesFromClaims.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importPoliciesFromClaims.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(importPoliciesFromClaims.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(syncClaimFormPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncClaimFormPolicies.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(syncClaimFormPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { selectPolicy, clearSelectedPolicy, clearError } = policySlice.actions;

export const selectPolicies = (state) => state.policies.items;
export const selectSelectedPolicy = (state) => state.policies.selectedPolicy;
export const selectPoliciesLoading = (state) => state.policies.loading;
export const selectPoliciesError = (state) => state.policies.error;
export const selectPoliciesPagination = (state) => state.policies.pagination;

export default policySlice.reducer;
